import React, { useEffect, useState } from 'react';
import { Lock, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../../lib/apiClient';
import { dataBackend, supabase } from '../../lib/supabaseClient';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const LoginForm = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaStage, setMfaStage] = useState('login');
  const [factorId, setFactorId] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [totpSecret, setTotpSecret] = useState(null);

  const supabaseLogin = dataBackend === 'supabase' && supabase;

  useEffect(() => {
    if (!supabaseLogin) return undefined;

    const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const query = new URLSearchParams(window.location.search);
    const linkType = params.get('type') || query.get('type');
    if (!['invite', 'recovery'].includes(linkType)) return undefined;

    let active = true;
    const preparePasswordSetup = async () => {
      const { data, error: sessionError } = await supabase.auth.getSession();
      if (!active) return;
      if (sessionError || !data.session) {
        setError('Il link non è valido o è scaduto. Richiedi un nuovo invito.');
        return;
      }
      setUsername(data.session.user.email || '');
      setMfaStage('set-password');
    };
    void preparePasswordSetup();

    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (!active || !session || !['SIGNED_IN', 'PASSWORD_RECOVERY'].includes(event)) return;
      setUsername(session.user.email || '');
      setMfaStage('set-password');
    });

    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
  }, [supabaseLogin]);

  const completeSupabaseLogin = async (accessToken) => {
    const response = await apiFetch(`${API_URL}/api/admin/me`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!response.ok) throw new Error('Account non autorizzato per l’area amministrativa');
    onLogin(accessToken);
  };

  const prepareMfa = async (session) => {
    const assurance = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (assurance.error) throw assurance.error;
    if (assurance.data.currentLevel === 'aal2') {
      await completeSupabaseLogin(session.access_token);
      return;
    }
    const factors = await supabase.auth.mfa.listFactors();
    if (factors.error) throw factors.error;
    const verified = factors.data.totp.find((factor) => factor.status === 'verified');
    if (verified) {
      setFactorId(verified.id);
      setMfaStage('verify');
      return;
    }
    const enrollment = await supabase.auth.mfa.enroll({ factorType: 'totp', friendlyName: 'Nicola Scerni Admin' });
    if (enrollment.error) throw enrollment.error;
    setFactorId(enrollment.data.id);
    setQrCode(enrollment.data.totp.qr_code);
    setTotpSecret(enrollment.data.totp.secret);
    setMfaStage('enroll');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (supabaseLogin && mfaStage === 'set-password') {
        if (password.length < 12) throw new Error('La password deve contenere almeno 12 caratteri');
        if (!/[a-z]/.test(password) || !/[A-Z]/.test(password) || !/\d/.test(password) || !/[^A-Za-z0-9]/.test(password)) {
          throw new Error('Usa almeno una minuscola, una maiuscola, un numero e un simbolo');
        }
        if (password !== passwordConfirm) throw new Error('Le password non coincidono');
        const update = await supabase.auth.updateUser({ password });
        if (update.error) throw update.error;
        const session = await supabase.auth.getSession();
        if (!session.data.session) throw new Error('Sessione non disponibile');
        window.history.replaceState({}, document.title, window.location.pathname);
        await prepareMfa(session.data.session);
        return;
      }

      if (supabaseLogin && mfaStage !== 'login') {
        if (!/^\d{6}$/.test(mfaCode)) throw new Error('Inserisci il codice a 6 cifre');
        const verification = await supabase.auth.mfa.challengeAndVerify({ factorId, code: mfaCode });
        if (verification.error) throw verification.error;
        const session = await supabase.auth.getSession();
        if (!session.data.session) throw new Error('Sessione non disponibile');
        await completeSupabaseLogin(session.data.session.access_token);
        return;
      }

      if (supabaseLogin) {
        const result = await supabase.auth.signInWithPassword({ email: username.trim(), password });
        if (result.error || !result.data.session) throw result.error || new Error('Credenziali non valide');
        await prepareMfa(result.data.session);
        return;
      }

      const response = await apiFetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      if (!response.ok) {
        throw new Error('Credenziali non valide');
      }

      const data = await response.json();
      localStorage.setItem('admin_token', data.access_token);
      onLogin(data.access_token);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0b114e] flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#d4883a]/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-[#d4883a]" />
          </div>
          <h1 className="text-2xl font-bold text-[#0b114e]">Admin Panel</h1>
          <p className="text-gray-500">Nicola Scerni</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {mfaStage === 'set-password' && (
            <p className="text-sm text-gray-600 text-center">
              Crea la password dell’account amministratore. Subito dopo configurerai l’autenticazione a due fattori.
            </p>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{supabaseLogin ? 'Email' : 'Username'}</label>
            <input
              type={supabaseLogin ? 'email' : 'text'}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4883a]/50 focus:border-[#d4883a]"
              required
              readOnly={mfaStage === 'set-password'}
              data-testid="admin-username-input"
            />
          </div>
          {(mfaStage === 'login' || mfaStage === 'set-password') && <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {mfaStage === 'set-password' ? 'Nuova password' : 'Password'}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4883a]/50 focus:border-[#d4883a]"
              required
              data-testid="admin-password-input"
            />
          </div>}

          {mfaStage === 'set-password' && <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Conferma password</label>
            <input
              type="password"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d4883a]/50 focus:border-[#d4883a]"
              required
              autoComplete="new-password"
            />
          </div>}

          {['enroll', 'verify'].includes(mfaStage) && (
            <div className="space-y-4">
              {mfaStage === 'enroll' && (
                <div className="text-center space-y-3">
                  <p className="text-sm text-gray-600">Scansiona il QR con la tua app di autenticazione. L’MFA è obbligatoria per l’area admin.</p>
                  {qrCode && <img src={qrCode} alt="QR per configurare MFA" className="w-48 h-48 mx-auto" />}
                  {totpSecret && <p className="text-xs break-all text-gray-500">Codice manuale: {totpSecret}</p>}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Codice MFA</label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-center tracking-[0.4em] focus:ring-2 focus:ring-[#d4883a]/50 focus:border-[#d4883a]"
                  required
                />
              </div>
            </div>
          )}

          {error && (
            <p className="text-red-500 text-sm text-center" data-testid="admin-login-error">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#d4883a] hover:bg-[#d4896a] text-white font-semibold py-3 rounded-lg transition-colors disabled:opacity-50"
            data-testid="admin-login-submit"
          >
            {loading ? 'Verifica...' : mfaStage === 'login' ? 'Accedi' : mfaStage === 'set-password' ? 'Imposta password' : 'Verifica MFA'}
          </button>
        </form>
        
        <div className="mt-6 text-center">
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-gray-500 hover:text-[#0b114e] transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Torna alla home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;
